import React from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import { registerSW } from 'virtual:pwa-register';
import reportWebVitals from './reportWebVitals';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { setupIonicReact } from '@ionic/react';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import {
    QueryClientProvider,
} from '@tanstack/react-query'
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { queryClient } from './query/queryClient';
import { installCurrentUserQueryListener } from './query/currentUserQuery';
import { installGatewayConnectionStateQueryListener } from './query/gatewayConnectionStateQuery';
import { installPushNotificationContextSync } from './client/OCPushNotificationContextSync';
import { installWatchSync } from './client/OCWatchSync';
import { initializeAnalytics, installGlobalErrorReporting, installScreenTracking, trackEvent } from './client/OCAnalytics';
import { AppErrorBoundary } from './components/AppErrorBoundary';
import { history } from './utils/history';
import { Capacitor } from '@capacitor/core';

setupIonicReact({
  innerHTMLTemplatesEnabled: true
});

CapacitorUpdater.notifyAppReady();

const container = document.getElementById('root');
const root = createRoot(container!);
const theme = createTheme({
  colorSchemes: {
    dark: true,
    light: false,
  },
});

initializeAnalytics();
installGlobalErrorReporting();
installScreenTracking(history);

installCurrentUserQueryListener(queryClient);
installGatewayConnectionStateQueryListener(queryClient);
installPushNotificationContextSync();
installWatchSync();

root.render(
    <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={theme}>
            <AppErrorBoundary>
                <App />
            </AppErrorBoundary>
        <ReactQueryDevtools initialIsOpen={false} />
        </ThemeProvider>
    </QueryClientProvider>
);

// Register service worker with Vite PWA
const updateSW = registerSW({
  onNeedRefresh() {
    console.log('New content available, please refresh.');
  },
  onOfflineReady() {
    console.log('App ready to work offline.');
  },
});

// Core Web Vitals matter for the PWA only; the store apps run in a webview
// where these thresholds don't apply.
if (!Capacitor.isNativePlatform()) {
    reportWebVitals(metric => trackEvent({
        name: 'web_vitals',
        params: {
            metric: metric.name,
            value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
            rating: metric.rating
        }
    }));
}
